import { useCallback, useEffect, useState } from 'react'

/**
 * The route, from the URL hash (`#/content`).
 *
 * HASH routing, not the History API — a deliberate difference from the viewer, which uses
 * History (docs: the Webv2 interaction model) because its URLs are user-facing and shareable.
 * The admin's are neither, and hash routing buys one thing that matters here: a Cloudflare Pages
 * project serves `index.html` only for paths it knows, so a History-routed `/content` deep-link
 * or refresh would 404 unless we also ship and maintain a SPA fallback rule. A hash never leaves
 * the document, so a refresh on any screen simply works. (This is also why the viewer's own
 * in-app routes are `viewermain.html#/categories` rather than bare paths.)
 */
export function useHashRoute(): { route: string; navigate: (id: string) => void } {
  const [route, setRoute] = useState(() => currentRoute())

  useEffect(() => {
    const onChange = () => setRoute(currentRoute())
    window.addEventListener('hashchange', onChange)
    return () => window.removeEventListener('hashchange', onChange)
  }, [])

  // Assigning the hash pushes a history entry, so the browser Back button walks the portal —
  // no separate nav stack to keep in step with the URL.
  const navigate = useCallback((id: string) => {
    window.location.hash = `#/${id}`
  }, [])

  return { route, navigate }
}

/** The current route id, stripped of `#/`. Empty string when there is no hash. */
export function currentRoute(): string {
  return window.location.hash.replace(/^#\/?/, '')
}
